import { useQuery } from '@tanstack/react-query'
import { controlCenterApi, type OverviewResponse } from '@/lib/api'
import { Boxes, Container, Server, ShieldCheck } from 'lucide-react'

type CardSpec = {
  label: string
  value: number | string
  detail?: string
  icon: typeof Boxes
}

export default function OverviewPage() {
  const { data, isLoading, isError } = useQuery<OverviewResponse>({
    queryKey: ['control-center', 'overview'],
    queryFn: controlCenterApi.getOverview,
  })

  const cards: CardSpec[] = [
    {
      label: 'Services registered',
      value: data?.servicesTotal ?? '—',
      icon: Boxes,
    },
    {
      label: 'Containers running',
      value: data ? `${data.containersRunning} / ${data.containersTotal}` : '—',
      icon: Container,
    },
    {
      label: 'Hosts reachable',
      value: data ? `${data.hostsUp} / ${data.hostsTotal ?? data.hostsUp}` : '—',
      icon: Server,
    },
    {
      label: 'Certs expiring < 30d',
      value: data?.certsExpiringSoon ?? '—',
      detail: data && data.certsExpiringSoon > 0 ? 'Action recommended' : undefined,
      icon: ShieldCheck,
    },
  ]

  return (
    <div className="p-8 space-y-6">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">Overview</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Snapshot of the deployed environment.
        </p>
      </header>

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load overview. Backend endpoint <code>/api/control-center/overview</code> may not yet be wired.
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, detail, icon: Icon }) => (
          <div
            key={label}
            className="rounded-lg border border-border bg-card p-5 flex items-start justify-between"
          >
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}
              </div>
              <div className="mt-2 text-3xl font-semibold">
                {isLoading ? '…' : value}
              </div>
              {detail ? <div className="mt-1 text-xs text-muted-foreground">{detail}</div> : null}
            </div>
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
        ))}
      </div>
    </div>
  )
}
